// React
import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useHistory } from 'react-router'

// Redux
import { RootState } from '../../store/reducer'
import { useDispatch, useSelector } from 'react-redux'

// Actions
import { fetchVenueInfoData, venueInfoUpdated } from '../../store/venueInfo'

// Axios instances
import { venueManagerApi } from '../../api/axios.config'

// Styles
import { ContainerStyles } from './styles'
import { GlobalStyle } from '../../styles/global'

// Components
import TagmeModal from '../../Components/TagmeModal'
import WaitlistHeader from '../../Components/WaitlistHeader'
import ServiceSelector from '../../Components/ServiceSelector'
import RemoteWaitlistForm from '../../Components/RemoteWaitlistForm'
import VenueUnavailable from '../../Components/VenueUnavailable'

// Errors
import { ErrorsList } from '../../common/errors'
import * as Sentry from '@sentry/react'

// Interfaces
import { RemoteWailistProps } from './interfaces'
import { ModalProps, ModalTypes } from '../../Components/TagmeModal/interface'
import { SuccessUpdateVenueInfo } from '../../store/interfaces'

// Utils
import { handleSessionStorage } from '../../utils/sessionStorageManagement'
import { COLORS } from '../../common/constants'

import { venuesListSpecial, soulList, ipirangaList, hidePreferredWaitlistTextList, camaroteBrasilList } from '../../common/textDinamic'

import { useTranslation } from 'react-i18next'
import OTPForm from '../../Components/OTPForm'
import { TrackEventsType, useCurrentUser } from '../../Services/current-user.service'

import { resolveClosedWaitlistRedirectUrl } from '../../utils/hubRedirect'

function RemoteWaitList() {
	const history = useHistory()
	const [stepData, setStepData] = useState<{ step: number, form: any }>({ step: 0, form: {} });

	const [modalInfo, setModalInfo] = useState<ModalProps>({
		show: false,
		type: ModalTypes.error,
	})

	// Extract the route param venueId from the url
	const { venueId } = useParams<RemoteWailistProps>()

	const [venueSlug, setVenueSlug] = useState<string>('')
	const [hasLoginJourney, setHasLoginJourney] = useState(false);
	// Venue desativada (Venues.disabled): não permite entrar na fila
	const [venueDisabled, setVenueDisabled] = useState(false)
	useEffect(() => {
		const fetchLoginJourneyFlag = async () => {
			try {
				const res = await venueManagerApi.get(`/venues/${venueId}`)
				const venueData = res.data
				if (venueData?.slug || venueData?.slug_am) {
					setVenueSlug(venueData.slug_am || venueData.slug)
				}
				const isAmWaitlist = !!(
					venueData?.has?.appsWaitlist ||
					venueData?.appsWaitlist ||
					venueData?.am_waitlist
				)
				if (isAmWaitlist) {
					const redirectId = venueData?.slug_am || venueData?.externalId || venueId
					window.location.replace(`https://lista.tagme.com.br/${redirectId}`)
					return
				}

				const loginJourneyEnabled = !!res.data?.hiredServices?.loginJourney
				setHasLoginJourney(loginJourneyEnabled)
				setVenueDisabled(res.data?.disabled === true)
			} catch (err) {
				console.warn('Failed to fetch loginJourney flag', err)
				setHasLoginJourney(false)
			}
		};
		fetchLoginJourneyFlag()
	}, [venueId]);


	const dispatch = useDispatch()

	//console.log("AAAAAAAAAAAAA")
	const { registerEvent } = useCurrentUser()

	// Access the globalState venueInfo
	const venueInfo = useSelector((state: RootState) => state.entities.venueInfo)

	//Handle the text translation
	const { t, i18n } = useTranslation()

	// Function for redirecting the client to the smartLink page or back to the Hub
	const handleToSmartlink = useCallback(() => {
		const targetUrl = resolveClosedWaitlistRedirectUrl({
			venueId,
			slug: venueInfo.slug || venueSlug,
			language: i18n.language,
		})
		window.location.href = targetUrl
	}, [venueId, venueInfo.slug, venueSlug, i18n.language])

	const closeModal = useCallback(() => {
		setModalInfo(previousModalInfoState => ({ ...previousModalInfoState, show: false }))
	}, [])

	const fetchStatusData = useCallback(async () => {
		try {
			// Fetch the information about the systems availability
			const waitlistStatusResponse = await venueManagerApi.get(`/waitlists/${venueId}/waitlist-statuses`)

			const { locked, active } = waitlistStatusResponse.data.waitlist

			const closed = locked || !active

			if (closed) {
				setModalInfo({
					show: true,
					closable: false,
					type: ModalTypes.noIcon,
					message: locked
						? `${t('Remote waitlist closed.')}`
						: `${t('No waitlist available at the moment.')}`,
					buttons: [
						{
							label: 'OK',
							action: handleToSmartlink,
						},
					],
				})

				return false
			}

			return true
		} catch (error) {
			setModalInfo({
				show: true,
				onClose: closeModal,
				type: ModalTypes.error,
				message: 'Venue not found.',
				buttons: [
					{
						label: 'OK',
						action: () => history.push(`/404-not-found`),
					},
				],
			})

			Sentry.setTag('venueId', venueId)
			Sentry.captureException(new Error(ErrorsList.FAILED_TO_GET_WAITLIST_STATUS))

			return false
		}
	}, [closeModal, handleToSmartlink, history, t, venueId])

	// It checks the venue availability
	useEffect(() => {
		fetchStatusData()
	}, [fetchStatusData])

	// Update the venueInfo
	useEffect(() => {
		async function fetchVenueData() {
			try {
				// Fetch the information available in the sessionStorage
				const { sessionStorageFilled, sessionStorageId, parsedSessionStorage } =
					handleSessionStorage('venueInfo')

				// The conditionals bellow only will be checked if the venueInfo (global state) is not filled
				// If the sessionStorage is properly filled, the venueInfo will be set based on this information,
				// avoiding unncessary resquests
				if (sessionStorageFilled && sessionStorageId === venueId) {
					return dispatch(venueInfoUpdated(parsedSessionStorage))
				}

				// Thunk Action for fetching data in the API and updating the global state venueInfo
				const successUpdateVenueInfo = (await dispatch(fetchVenueInfoData(venueId))) as SuccessUpdateVenueInfo

				if (successUpdateVenueInfo.errorsMessages.length > 0) {
					let hasOnlyThemeError = false

					for (const errorMessage of successUpdateVenueInfo.errorsMessages) {
						hasOnlyThemeError =
							successUpdateVenueInfo.errorsMessages.length === 1 && errorMessage.includes('THEME')
						Sentry.setTag('venueId', venueId)
						Sentry.captureMessage(errorMessage, Sentry.Severity.Warning)
					}

					if (!hasOnlyThemeError) {
						setModalInfo({
							show: true,
							closable: false,
							type: ModalTypes.noIcon,
							message: 'Waitlist is currently inaccessible.',
							buttons: [
								{
									label: 'OK',
									action: handleToSmartlink,
								},
							],
						})
					}
				}
			} catch (error) {
				Sentry.setTag('venueId', venueId)
				Sentry.captureException(new Error(ErrorsList.UNKNOWN_ERROR))
			}
		}

		// If the venueInfo is filled, we don't need to proceed, so the fetchVenueData function bellow won't be called
		if (!venueInfo._id) {
			fetchVenueData()
		}
	}, [closeModal, dispatch, handleToSmartlink, history, venueId, venueInfo._id])

	const handlePriorityDisclaimer = () => {
		let priorityText = ''

		// Não exibe o texto para venues na lista ipirangaList ou hidePreferredWaitlistTextList
		if (ipirangaList.includes(venueInfo._id as string) ||
			hidePreferredWaitlistTextList.includes(venueInfo._id as string)) {
			return null
		}

		if (venuesListSpecial.includes(venueInfo._id as string)) {
			priorityText = `${t(
				'In case of priority, look for one of our attendants when you arrive at the establishment.'
			)}`
		} else if (soulList.includes(venueInfo._id as string) || ipirangaList.includes(venueInfo._id as string)) {
			priorityText = `${t('In case of priority, look for one of our attendants when you arrive.')}`
		} else if (soulList.includes(venueInfo._id as string) || camaroteBrasilList.includes(venueInfo._id as string)) {
			priorityText = `${t('In case of priority, please contact one of our concierges.')}`
		} else {
			priorityText = `${t(
				'In case of priority, look for one of our attendants when you arrive at the restaurant.'
			)}`
		}

		return (
			<div id="div_priority_disclaimer">
				<p>{priorityText}</p>
			</div>
		)
	}

	const checkReservationLock = (venueId: string, number: string): boolean => {
		const normalizedNumber = number.replace(/\D+/g, '');
		const sessionRaw = localStorage.getItem('sessionLock');
		const session = sessionRaw ? JSON.parse(sessionRaw) : {};

		const key = `${venueId}:${normalizedNumber}`;
		const lockedAt = session[key];

		if (!lockedAt) return false;

		const now = Date.now();
		const lockedTime = new Date(lockedAt).getTime();

		if (isNaN(lockedTime) || (now - lockedTime) > 10 * 60 * 1000) {
			delete session[key];
			localStorage.setItem('sessionLock', JSON.stringify(session));
			return false;
		}

		return true;
	}

	const lockReservation = (venueId: string, number: string): void => {
		const normalizedNumber = number.replace(/\D+/g, '');
		const sessionRaw = localStorage.getItem('sessionLock');
		const session = sessionRaw ? JSON.parse(sessionRaw) : {};

		session[`${venueId}:${normalizedNumber}`] = new Date().toISOString();

		localStorage.setItem('sessionLock', JSON.stringify(session));
	}

	const enterWaitlist = async (data: any) => {
		try {
			// Effectivelly post the data and insert the user in the waitlist
			const response = await venueManagerApi.post(`/waitlists/${venueInfo._id}/create`, data)


			// Extracts the waitlistId from the response data
			const waitlistId = response.data._id

			let secondLineText = 'Look for the receptionist when you arrive.'

			if (ipirangaList.includes(venueInfo._id as string)) secondLineText = ''

			if (venueInfo._id) {
				registerEvent(venueInfo._id, TrackEventsType.WAITLIST_ENTER, {
					data,
					waitlistId
				})
			}

			if (waitlistId) {
				lockReservation(venueInfo._id!, data.customer.phone)
				setModalInfo({
					show: true,
					closable: false,
					type: ModalTypes.success,
					title: t("You've joined the waitlist"),
					message: [
						"We've sent a SMS with a link for you to follow your position in the waitlist.",
						secondLineText,
					],
					buttons: [
						{
							label: 'Follow',
							action: () => {
								history.push(`/waitlist/${waitlistId}`)
							},
						},
					],
				})
			}
		} catch (err: any) {
			// A API recusa a criação quando a venue foi desativada com o widget já aberto
			if (err?.response?.status === 403 && err?.response?.data?.code === 'VENUE_DISABLED') {
				setVenueDisabled(true)
				return
			}

			setModalInfo({
				show: true,
				onClose: closeModal,
				type: ModalTypes.error,
				message: 'Failed to create waitlist.',
				buttons: [
					{
						label: 'OK',
						action: closeModal,
					},
				],
			})

			Sentry.setTag('venueId', venueInfo._id)
			Sentry.captureException(new Error(ErrorsList.FAILED_TO_CREATE_NEW_WAITLIST))
		}
	}


	// 	<RemoteWaitlistForm fetchStatusData={fetchStatusData} />

	if (venueDisabled) return <VenueUnavailable />

	return (
		<ContainerStyles
			colorBackground={venueInfo.color.background || COLORS.background}
			colorBorder={venueInfo.color.border || COLORS.border}
		>
			<WaitlistHeader />


			{
				stepData.step === 0 ?
					(<>

						<ServiceSelector />

						{handlePriorityDisclaimer()}

						<RemoteWaitlistForm hasLoginJourney={hasLoginJourney}
							fetchStatusData={fetchStatusData}
							enterWaitlist={enterWaitlist}
							validateUser={(form: any) => setStepData({ step: 1, form })}
							checkReservationLock={checkReservationLock}
						/>
					</>) :

					<OTPForm form={stepData.form || {}}
						enterWaitlist={() => enterWaitlist(stepData.form)}
						onReturnClick={() => setStepData({ step: 0, form: stepData.form })}
					/>
			}


			<TagmeModal {...modalInfo} />

			<GlobalStyle
				colorBackground={venueInfo.color.background || COLORS.background}
				modalOpened={modalInfo.show}
			/>
		</ContainerStyles>
	)
}

export default RemoteWaitList
